import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClipboardList } from 'lucide-react';
import { RichTextEditor } from '@/components/rich-text';
import type { TaskRaw } from '@/types';

export type TaskUpsertPayload = {
  id?: number;
  title: string;
  description: string;
  estimated_hours: number;
  priority?: string;
  due_date?: string | null;
  assignee_name?: string | null;
  status: 'todo' | 'doing' | 'done';
};

interface TaskUpsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: TaskUpsertPayload) => Promise<void>;
  initialData?: TaskRaw | null;
  defaultStatus?: 'todo' | 'doing' | 'done';
  isExternal?: boolean;
}

export function TaskUpsertDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  defaultStatus = 'todo',
  isExternal,
}: TaskUpsertDialogProps) {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [estimatedHours, setEstimatedHours] = React.useState<number>(0);
  const [priority, setPriority] = React.useState<string>('medium');
  const [dueDate, setDueDate] = React.useState<string>('');
  const [assigneeName, setAssigneeName] = React.useState('');
  const [status, setStatus] = React.useState<'todo' | 'doing' | 'done'>(
    defaultStatus
  );

  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      if (initialData) {
        setTitle(initialData.title || initialData.tasks || '');
        setDescription(
          initialData.description || initialData.task_description || ''
        );
        setEstimatedHours(Number(initialData.estimated_hours) || 0);
        setPriority(initialData.priority || 'medium');
        setDueDate(
          initialData.due_date
            ? new Date(initialData.due_date).toISOString().split('T')[0]
            : ''
        );
        setAssigneeName(initialData.assignee_name || '');

        // Map status
        const s = initialData.status;
        if (s === 3 || s === 'done') setStatus('done');
        else if (s === 2 || s === 'doing') setStatus('doing');
        else setStatus('todo');
      } else {
        setTitle('');
        setDescription('');
        setEstimatedHours(0);
        setPriority('medium');
        setDueDate('');
        setAssigneeName('');
        setStatus(defaultStatus);
      }
    }
  }, [open, initialData, defaultStatus]);

  const handleSave = async () => {
    if (!title.trim()) return;

    try {
      setIsSaving(true);
      await onSubmit({
        id: initialData?.id,
        title: title.trim(),
        description,
        estimated_hours: estimatedHours,
        priority,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        assignee_name: assigneeName.trim() || null,
        status,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save task:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-border/40 bg-background/95 backdrop-blur-xl shadow-2xl p-0 overflow-hidden">
        <DialogHeader className="p-8 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
              <ClipboardList className="w-6 h-6" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                {initialData ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
              <p className="text-sm text-muted-foreground font-medium">
                {initialData
                  ? 'Update task details and progress'
                  : 'Define a new task for this milestone'}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-8 pt-2 space-y-6 overflow-y-auto max-h-[70vh] custom-scrollbar">
          {/* Title Field */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              Task Title *
            </Label>
            <div className="relative group">
              <Input
                placeholder="What needs to be done?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>

          {initialData && !isExternal && (
            <div className="grid grid-cols-2 gap-6">
              {/* Status */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1 flex items-center gap-2">
                  Status
                </Label>
                <Select value={status} onValueChange={(v: any) => setStatus(v)}>
                  <SelectTrigger className="h-11 rounded-xl border-border/40 bg-muted/30">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border/40 backdrop-blur-xl">
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="doing">In Progress</SelectItem>
                    <SelectItem value="done">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Description / Rich Text */}
          <div className="space-y-3 pt-2">
            <Label className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              Task Description
            </Label>
            <div className="rounded-[1.5rem] border border-border/40 bg-muted/20 overflow-hidden ring-offset-background focus-within:ring-2 focus-within:ring-primary/20 transition-all">
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Detailed instructions, requirements, or notes..."
                minHeight={200}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="p-8 pt-4 border-t border-border/20 bg-muted/10">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Discard
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !title.trim()}>
            {isSaving
              ? 'Saving Changes...'
              : initialData
                ? 'Update Task'
                : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
